export type SharedDeviceMappedState =
  | { state: 'revoked'; nlsKey: 'NLSOpsSharedDeviceRevoked' }
  | { state: 'locked'; minutes: number; nlsKey: 'NLSOpsSharedDeviceLocked' }
  | { state: 'wrongPin'; triesLeft: number; nlsKey: 'NLSOpsSharedDeviceWrongPin' }
  | { state: 'noPin'; nlsKey: 'NLSOpsSharedDeviceNoPinYet' }
  | { state: 'notEligible'; nlsKey: 'NLSOpsSharedDeviceNotEligible' }
  | { state: 'other'; messageKey: 'NLSOpsSharedDeviceErrorEnrolFailed'; nlsKey: 'NLSOpsSharedDeviceErrorEnrolFailed' }

/**
 * Maps server error to shared device state based strictly on code + details.reason.
 * Never matches on English message substrings (F2 rule).
 */
export function mapSharedDeviceError(error: unknown): SharedDeviceMappedState {
  if (!error || typeof error !== 'object') {
    return {
      state: 'other',
      messageKey: 'NLSOpsSharedDeviceErrorEnrolFailed',
      nlsKey: 'NLSOpsSharedDeviceErrorEnrolFailed',
    }
  }

  const errObj = error as Record<string, unknown>
  const rawCode = typeof errObj.code === 'string' ? errObj.code : ''
  const code = rawCode.replace(/^functions\//, '')
  const details =
    errObj.details && typeof errObj.details === 'object'
      ? (errObj.details as Record<string, unknown>)
      : undefined

  const reason = details && typeof details.reason === 'string' ? details.reason : undefined

  // Keyed on code + details.reason only
  if (
    reason === 'DEVICE_REVOKED' ||
    reason === 'DEVICE_NOT_FOUND' ||
    reason === 'DEVICE_BAD_SECRET' ||
    reason === 'DEVICE_OTHER_WORKSPACE'
  ) {
    return {
      state: 'revoked',
      nlsKey: 'NLSOpsSharedDeviceRevoked',
    }
  }

  if (reason === 'PIN_LOCKED' || (code === 'resource-exhausted' && reason === 'PIN_LOCKED')) {
    let minutes = 15
    if (typeof details?.lockedUntil === 'number') {
      const remainingMs = details.lockedUntil - Date.now()
      if (remainingMs > 0) {
        minutes = Math.max(1, Math.ceil(remainingMs / 60000))
      }
    }
    return {
      state: 'locked',
      minutes,
      nlsKey: 'NLSOpsSharedDeviceLocked',
    }
  }

  if (reason === 'PIN_WRONG') {
    const triesLeft = typeof details?.triesLeft === 'number' ? details.triesLeft : 0
    return {
      state: 'wrongPin',
      triesLeft,
      nlsKey: 'NLSOpsSharedDeviceWrongPin',
    }
  }

  if (reason === 'NO_PIN') {
    return {
      state: 'noPin',
      nlsKey: 'NLSOpsSharedDeviceNoPinYet',
    }
  }

  if (reason === 'NOT_ELIGIBLE') {
    return {
      state: 'notEligible',
      nlsKey: 'NLSOpsSharedDeviceNotEligible',
    }
  }

  return {
    state: 'other',
    messageKey: 'NLSOpsSharedDeviceErrorEnrolFailed',
    nlsKey: 'NLSOpsSharedDeviceErrorEnrolFailed',
  }
}
